/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import PresetLabelsPanel from './PresetLabelsPanel';
import { useWorkspaceLabelsConfig } from '../../api/workspace-api/hooks';
import type { WorkspaceSupportedComponentTypes } from '../../store/viewPresets/types';
import type { WorkspacePresetLabels } from '../../store/workspace/types';
import { useWorkspaceTranslation } from '../../utils/i18n';

interface PresetLabelsPanelWrapperProps {
  viewComponentType?: WorkspaceSupportedComponentTypes;
  onLabelsChange?: (labels: WorkspacePresetLabels) => void;
  menuLabels?: WorkspacePresetLabels;
  isMenuFilterMode?: boolean;
}

const PresetLabelsPanelWrapper: React.FC<PresetLabelsPanelWrapperProps> = ({
  viewComponentType,
  onLabelsChange,
  menuLabels,
  isMenuFilterMode = false,
}) => {
  const { t } = useWorkspaceTranslation();
  const {
    data: labelsConfig,
    isFetching: isLoading,
    error: labelsError,
    refetch: refetchLabelsConfig,
  } = useWorkspaceLabelsConfig();

  if (isLoading) {
    return (
      <LinearProgress
        color="secondary"
        data-testid="labelspanel-loading-bar"
        sx={{ marginBottom: '-4px' }}
      />
    );
  }

  if (labelsError || labelsConfig === undefined) {
    return (
      <AlertBanner
        actionButtonProps={{
          title: t('fetch-config-error-retry'),
          onClick: () => {
            void refetchLabelsConfig();
          },
        }}
        info={
          labelsError
            ? labelsError.message
            : t('fetch-config-no-labels-available')
        }
        severity="error"
        title={t('fetch-config-error-title')}
      />
    );
  }

  return (
    <PresetLabelsPanel
      isMenuFilterMode={isMenuFilterMode}
      labelsConfig={labelsConfig!}
      menuLabels={menuLabels}
      onLabelsChange={onLabelsChange}
      viewComponentType={viewComponentType}
    />
  );
};

export default PresetLabelsPanelWrapper;
